import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import Skeleton from '../../components/Skeleton';
import classNames from 'classnames';
import SearchLgSvg from '../../assets/icons0/search-lg.svg';
import XCircleSvg from '../../assets/icons0/x-circle.svg';
import AlgoliaLogoBlueSvg from '../../assets/algolia-logo-blue.svg';
import Masonry from '../../components/service-list/masonry';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import {ErrorBoundary} from 'react-error-boundary';
import {useLocation, useNavigate} from 'react-router-dom';
import searchFilter from '../../searchFilter';
import SuspenseList from '../../components/SuspenseList';
import useQuery from '../../useQuery';
import Button from '../../components/button/button';
import ErrorFallback from '../../components/ErrorFallback';
import {useForm} from 'react-hook-form';
import ServiceList from '../../components/service-list/service-list';
import {useWdl} from '../../components/wdl';
import CtaSellOnline from '../../components/cta-sell-online/cta-sell-online';
import EventCard from '../../components/event-card/event-card';
import CtaSocials from '../../components/cta-socials/cta-socials';
import './events.scss';

const breakpointCols = {default: 3, 767: 1};
const SearchServicesImpl = React.memo(({viewer}) => {
    const {data, loadNext, hasNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql`
            fragment eventsPagesServicesImpl_viewer on Viewer @refetchable(queryName: "eventsPagesServicesImplQuery") {
                searchServices(first: $first, after: $after, contain: $contain) @connection(key: "eventsPagesServices_searchServices") {
                    edges {
                        node {
                            score {
                                highlightResult
                                snippetResult
                            }
                            service {
                                id
                                slug
                                name
                                nameUk
                                details
                                detailsUk
                                location
                                locationUk
                                start
                                end
                                strategy
                                every
                                warp
                                target
                                description
                                pin
                            }
                        }
                    }
                    searchResult {
                        nbHits
                        queryID
                    }
                }
            }
        `,
        viewer
    );
    const {contain} = useQuery();
    const [isPending, startTransition] = React.useTransition();
    const isMount = React.useRef(true);
    React.useEffect(() => {
        if (isMount.current) {
            isMount.current = false;
        } else {
            startTransition(() => {
                refetch({contain});
            });
        }
    }, [contain, isMount]);
    const wdl = useWdl();
    const prevIsLoadingNextRef = React.useRef(isLoadingNext);
    React.useEffect(() => {
        if (prevIsLoadingNextRef.current === true && isLoadingNext === false) {
            wdl?.set?.('event', 'Hits Viewed');
        }
        prevIsLoadingNextRef.current = isLoadingNext;
    }, [prevIsLoadingNextRef, isLoadingNext]);
    return (
        <>
        <div className='position-relative background-color-gray-50'>
            <div className='block main-title--block'>
                <div className='padding-top-2rem main-title--container'>
                    <h1 className='color-primary-600 text-sm medium margin-bottom-0dot75rem'>
                        <span className='text-sm mw768-text-md semibold color-blue-gray-500'>
                            <FormattedMessage defaultMessage='Events | Ottry Tickets'/>
                        </span>
                    </h1>
                    <h2 className='display-lg semibold color-gray-900'>
                        {data.searchServices.searchResult?.nbHits || '0'}{' '}майбутніх подій
                    </h2>
                </div>
            </div>
        </div>
        <div className='background-color-gray-50 padding-top-1dot5rem mw768-padding-top-2rem'>
            <div className='view'>
                <div className='block'>
                    <SuspenseList revealOrder='forwards'>
                        <Masonry
                            breakpointCols={breakpointCols}
                            className='masonry-grid'
                            columnClassName='masonry-grid_column'
                        >
                            {data.searchServices.edges.map(e => e.node).map((scoreService, index) => {
                                if (!scoreService.service) {
                                    return null;
                                }
                                return (
                                    <div 
                                        key={scoreService.service.id}
                                        data-insights-object-id={scoreService.service.id}
                                        data-service-slug={scoreService.service.slug}
                                        data-insights-position={index + 1}
                                        data-insights-query-id={data.searchServices.searchResult?.queryID}
                                    >
                                        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                            <React.Suspense fallback={
                                                <div className='background-color-white border-radius-0dot75rem padding-left-1dot5rem padding-top-1dot5rem padding-right-1dot5rem padding-bottom-1dot5rem shadow-large-light'>
                                                    <Skeleton/>
                                                </div>
                                            }>
                                                <EventCard {...{service: scoreService.service, highlightResult: scoreService.score?.highlightResult, snippetResult: scoreService.score?.snippetResult, highlightCover: true}} />
                                            </React.Suspense>
                                        </ErrorBoundary>
                                    </div>
                                );
                            })}
                        </Masonry>
                        <div/>
                    </SuspenseList>
                    {hasNext &&
                        <div className='margin-top-1dot5rem display-flex justify-content-center'>
                            <Button {...{size: 'xl', color:'secondary-gray', disabled: isLoadingNext, clickHandler: () => !isLoadingNext && loadNext(number || 8)}}>
                                <FormattedMessage defaultMessage='More events' />
                                {isLoadingNext && '...'}
                            </Button>
                        </div>
                    }
                </div>
            </div>
        </div>
        </>
    );
});

const number = process.env.NODE_ENV === 'development' ? 2 : null;
const SearchServices = React.memo(({containDeferred}) => {
    const {contain} = useQuery();
    const containMemo = React.useMemo(() => contain, []);
    const data = useLazyLoadQuery(
        graphql`
            query eventsPagesServicesQuery($first: Int!, $after: String, $contain: String) {
                viewer {
                    ...eventsPagesServicesImpl_viewer
                }
            }
        `,
        {first: number || 8, contain: containMemo}
    );
    const wdl = useWdl();
    React.useEffect(() => {
        wdl?.set?.('event', 'Hits Viewed');
    }, [containDeferred]);
    return (
        <SearchServicesImpl {...{viewer: data.viewer}}/>
    );
});

const Switcher = React.memo(() => {
    const {contain} = useQuery();
    const containDeferred = React.useDeferredValue(contain);
    const flag = contain !== containDeferred;
    return (
        <>
        <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
            <React.Suspense fallback={
                <div className='position-relative background-color-gray-50'>
                    <div className='block main-title--block'>
                        <div className='padding-top-2rem main-title--container'>
                            <Skeleton/>
                        </div>
                    </div>
                </div>
            }>
                <div className={classNames({'opacity-0dot6': flag})}>
                    {!!containDeferred ? 
                        <>
                        <SearchServices {...{containDeferred}}/> 
                        </>
                        : 
                        <>
                        <div className='position-relative background-color-gray-50'>
                            <div className='block main-title--block'>
                                <div className='padding-top-2rem main-title--container'>
                                    <h1 className='color-blue-gray-500 text-sm mw768-text-md semibold margin-bottom-0dot5rem mw768-margin-bottom-1rem'>
                                        <FormattedMessage defaultMessage='Events | Ottry Tickets'/>
                                    </h1>
                                    <h2 className='display-sm mw768-display-lg semibold color-gray-900'><FormattedMessage defaultMessage='All upcoming events'/></h2>
                                </div>
                            </div>
                        </div>
                        <div className='background-color-gray-50 padding-top-1dot5rem mw768-padding-top-2rem'>
                            <div className='view'>
                                <div className='block'>
                                    <ServiceList/>
                                </div>
                            </div>
                        </div>
                        </>
                    }
                </div>           
            </React.Suspense>
        </ErrorBoundary>
        </>
    );
});

const config = {shouldValidate: true, shouldDirty: true};
const Events = () => {
    const {contain, search = false} = useQuery();
    const location = useLocation();
    const navigate = useNavigate();
    const intl = useIntl();
    const {register, handleSubmit, setFocus, setValue, watch} = useForm({defaultValues: {contain}});
    const cntn = watch('contain');
    const submit = handleSubmit(data => {
        navigate(`${location.pathname}${searchFilter(location.search, {contain: data.contain}, false)}`);
    });
    React.useEffect(() => {
        if (search) {
            setFocus('contain');
        }
    }, []);
    return (
        <div>
            <div className='events position-relative background-color-gray-50'>
                <div className='padding-top-1dot5rem mw768-padding-top-6rem'>
                    <div className='view'>
                        <div className='block'>
                            <div className='display-flex'>
                                <div className='flex-1 mw768-flex-grow-0 mw768-flex-shrink-0 mw768-flex-basis-50percent'>
                                    <form onSubmit={submit}>
                                        <div className='form_beta'>
                                            <div className='display-flex column-gap-0dot5rem'>
                                                <div className='mw768-max-width-20rem width-100percent display-flex flex-direction-column row-gap-0dot25rem'>
                                                    <div className='input background-color-white border-1px-solid border-color-gray-300 border-radius-0dot5rem display-flex align-items-center padding-left-0dot875rem padding-top-0dot625rem padding-bottom-0dot625rem padding-right-0dot875rem'>
                                                        <SearchLgSvg
                                                            className='color-gray-500 cursor-pointer display-block width-1dot25rem height-1dot25rem'
                                                            onClick={() => {
                                                                submit();
                                                                setFocus('contain');
                                                            }}
                                                        />
                                                        <div className='flex-1 padding-left-0dot5rem padding-right-0dot5rem'>
                                                            <input
                                                                className='width-100percent text-md color-gray-500 display-block'
                                                                type='search'
                                                                placeholder={intl.formatMessage({defaultMessage: 'Search'})}
                                                                {...register('contain')}
                                                            />
                                                        </div>
                                                        {cntn && 
                                                        <XCircleSvg
                                                            className='color-gray-500 cursor-pointer display-block width-1dot25rem height-1dot25rem'
                                                            onClick={() => {
                                                                setValue('contain', '', config);
                                                                setFocus('contain');
                                                            }}
                                                        />
                                                        }
                                                    </div>
                                                    <div className='display-flex justify-content-flex-end'>
                                                        <AlgoliaLogoBlueSvg
                                                            className='display-block'
                                                            style={{width: 52.7, height: 12}}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='hide-small'>
                                                    <Button {...{size: 'lg', color: 'primary-gradient', type: 'submit'}}>
                                                        <FormattedMessage defaultMessage='Search'/>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Switcher/>
            <div className='background-color-gray-50'>
                <div className='view'>
                    <div className='block'>
                        <div className='padding-top-3rem mw768-padding-top-4rem padding-bottom-1rem mw768-padding-bottom-4rem'>
                            <CtaSocials/>
                        </div>
                    </div>
                </div>
            </div>
            <div className='gradient-grey-900-600-45deg-gradient-grey-800-700-26dot5deg'>
                <CtaSellOnline/>
            </div>
        </div>
    );
}

export default React.memo(Events);